import React, {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import ComponentConfig, {
  ComponentConfigProps,
  SaveButton,
  Footer,
} from 'organization/Event/DashboardConfig/ComponentConfigPanel'
import Settings from 'Event/template/Panels/Dashboard/EmojiList/EmojiListConfig/Settings'
import Styling from 'Event/template/Panels/Dashboard/EmojiList/EmojiListConfig/Styling'
import {usePanelsTemplate, usePanelsUpdate} from 'Event/template/Panels'
import {EmojiList} from 'Event/template/Panels/Dashboard/EmojiList'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import StylingPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/StylingPanel'
import RulesPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/RulesPanel'
import ScheduleInput from 'organization/Event/DashboardConfig/ComponentConfigPanel/ScheduleInput'
import {Rule} from 'Event/attendee-rules'
import {useAutoUpdate} from 'organization/Event/TemplateEditor'
import {
  Emoji,
  useDeleteEmojiFile,
} from 'Event/Dashboard/components/EmojiList/emoji'

export default function EmojiListConfig(props: ComponentConfigProps) {
  const template = usePanelsTemplate()
  const updateTemplate = usePanelsUpdate()
  const {emojiList} = template

  const {showing, onClose} = props
  const [emojiWidth, setEmojiWidth] = useState<number | null>(
    emojiList.emojiWidth,
  )
  const [emojis, setEmojis] = useState<Emoji['name'][]>(emojiList.emojis)
  const [customEmojis, setCustomEmojis] = useState<Emoji['name'][]>(
    emojiList.customEmojis ?? [],
  )
  const [rules, setRules] = useState<Rule[]>(emojiList.rules || [])
  const deleteFile = useDeleteEmojiFile()

  const {control, setValue} = useForm()

  useEffect(() => {
    if (showing) {
      return
    }

    setEmojiWidth(emojiList.emojiWidth)
    setEmojis(emojiList.emojis)
  }, [showing, emojiList])

  const save = () => {
    const data: EmojiList = {
      emojiWidth,
      emojis,
      customEmojis,
      rules,
    }

    updateTemplate({
      emojiList: data,
    })

    onClose()
  }

  const addCustomEmoji = (emojiName: Emoji['name']) => {
    const changedCustomEmojies = [...customEmojis, emojiName]
    setCustomEmojis(changedCustomEmojies)
    setEmojis((curr) => [...curr, emojiName])

    const data: EmojiList = {
      ...emojiList,
      customEmojis: changedCustomEmojies,
    }

    updateTemplate({
      emojiList: data,
    })
  }

  const removeCustomEmoji = (emojiName: Emoji['name']) => {
    // set current custom emojies
    const changedCustomEmojies = customEmojis.filter((e) => e !== emojiName)
    setCustomEmojis(changedCustomEmojies)

    // save custom emojies to API
    const data: EmojiList = {
      ...emojiList,
      customEmojis: changedCustomEmojies,
    }
    updateTemplate({
      emojiList: data,
    })

    // Delete Custom emoji file
    deleteFile(emojiName)

    // Remove all emojies with that custom emoji
    const removed = emojis.filter((e) => e !== emojiName)
    setEmojis(removed)
  }

  useAutoUpdate({
    values: {
      emojiList: {
        emojiWidth,
        emojis,
      },
    },
    when: showing,
  })

  return (
    <ComponentConfig showing={showing} onClose={onClose} title="Emojis">
      <SettingsPanel>
        <Settings
          emojis={emojis}
          setEmojis={setEmojis}
          customEmojis={customEmojis}
          addCustomEmoji={addCustomEmoji}
          removeCustomEmoji={removeCustomEmoji}
        />
      </SettingsPanel>
      <StylingPanel>
        <Styling emojiWidth={emojiWidth} setEmojiWidth={setEmojiWidth} />
      </StylingPanel>
      <RulesPanel rules={rules} setRules={setRules}>
        <ScheduleInput
          setValue={setValue}
          control={control}
          values={emojiList}
        />
      </RulesPanel>
      <Footer>
        <SaveButton onClick={save} />
      </Footer>
    </ComponentConfig>
  )
}
